import { useOidc } from '@revenuewell/logic-oidc';
import { useFeatureFlag } from '@revenuewell/logic-feature-flags';
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';

type State = { [key: string]: number | string | boolean | State };

type pathArg =
  | string
  | {
      pathname?: string;
      search: string;
      hash?: string;
      state?: State;
    };

function debugPush(event: State) {
  console.debug('Analytics Event:', event);
}

function stripParams(seg: string) {
  if (seg && seg.indexOf('?') >= 0) return seg.slice(0, seg.indexOf('?'));
  return seg;
}

function getStateBase(path: pathArg, locationId: any) {
  const startState = typeof path == 'string' ? undefined : path.state;
  const pathStr = typeof path == 'string' ? path : path.pathname ?? '';
  const segs = pathStr.split('/');
  let productArea = '';
  let subProductArea = '';
  let finalProductArea = ''; // only used in proxy frame

  if (segs.length > 1) productArea = segs[1];
  if (segs.length > 2) subProductArea = segs[2];
  if (segs.length > 3) finalProductArea = segs[3];

  if (productArea.includes('-frame')) {
    //this handles setting the product area and sub area for proxy frame
    productArea = subProductArea;
    subProductArea = finalProductArea;
  }

  return {
    ...startState,
    locationId: locationId,
    productArea: stripParams(productArea),
    subProductArea: stripParams(subProductArea)
  };
}

export const AnalyticsContext = createContext<{
  sendEvent: (eventName: string, data: State) => void;
}>(null!);

export default function AnalyticsProvider(props: React.PropsWithChildren<any>) {
  const { getClaims } = useOidc();
  const { featureFlags } = useFeatureFlag();
  const { search } = useLocation();
  const [loaded, setLoaded] = useState(false);
  const [userData, setUserData] = useState<State>();
  const history = useHistory();

  const sendEvent = useCallback(
    (eventName: string, data: State) => {
      const myWindow = (window as unknown) as {
        dataLayer: { push: (eventData: State) => void };
      };
      const baseState = getStateBase(
        document.location.pathname,
        userData?.locationId
      );
      myWindow.dataLayer = myWindow.dataLayer || { push: debugPush };
      myWindow.dataLayer.push({ ...baseState, ...data, event: eventName });
    },
    [userData]
  );

  useEffect(() => {
    //short circut
    if (userData || !featureFlags) return;

    const setup = async () => {
      const query = new URLSearchParams(search);
      const claims = await getClaims();
      const userClass = claims.userClass;
      const userId = claims.id;
      let customerId = claims.masterAccountId;
      if (!customerId) {
        try {
          customerId = query.get('customerId')
            ? parseInt(query.get('customerId')!)
            : undefined;
        } catch (e: unknown) {
          console.error('Error parsing customer id parameter');
        }
      }
      let locationId = claims.locationId as number | undefined;
      if (!locationId) {
        try {
          locationId = query.get('locationId')
            ? parseInt(query.get('locationId')!)
            : undefined;
        } catch (e: unknown) {
          console.error('Error parsing location id parameter');
        }
      }
      const eventData: State = {
        event: 'authentication',
        customerId: customerId!,
        userId: userId,
        userClass: userClass,
        locationId: locationId || 0,
        hasCore: featureFlags.includes('Core')
      };
      featureFlags.forEach((f) => {
        const prop = 'has' + f;
        eventData[prop] = true;
      });

      const myWindow = (window as unknown) as {
        dataLayer: { push: (eventData: State) => void };
      };
      myWindow.dataLayer = myWindow.dataLayer || { push: debugPush };
      myWindow.dataLayer.push(eventData);
      setUserData(eventData);
    };
    setup();
  }, [userData, getClaims, search, featureFlags]);

  useEffect(() => {
    if (!userData) return;

    const origPush = history.push;
    const origReplace = history.replace;

    history.push = (path: pathArg, state?: State) => {
      const baseState = getStateBase(path, userData.locationId);
      //this makes sure the path state can't override anything
      //this is required to make react NavLink work it send null as state
      //and overides the state passed in.  getBaseState merged it.
      if (typeof path != 'string') path.state = undefined;

      origPush(path, { ...baseState, ...state });
    };
    history.replace = (path: pathArg, state?: State) => {
      const baseState = getStateBase(path, userData.locationId);
      //this makes sure the path state can't override anything
      //this is required to make react NavLink work it send null as state
      //and overides the state passed in.  getBaseState merged it.
      if (typeof path != 'string') path.state = undefined;

      origReplace(path, { ...baseState, ...state });
    };
    setLoaded(true);
  }, [history, userData]);

  if (loaded) {
    return (
      <AnalyticsContext.Provider value={{ sendEvent: sendEvent }}>
        {props.children}
      </AnalyticsContext.Provider>
    );
  } else {
    return <></>;
  }
}

export function useAnalytics() {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within the AnalyticsProvider');
  }
  return context;
}
